import React, { useState, useEffect } from 'react';
import { createFlock, updateFlock } from '../../../services/butcherLogService';

function toDateInput(val) {
  if (!val) return '';
  const s = String(val);
  if (s.includes('T')) return s.slice(0, 10);
  return s.slice(0, 10);
}

function emptyMortality() {
  return { date: '', count: '', cause: '', stage: 'brooder' };
}

export default function FlockForm({ flock, onSaved, onCancel }) {
  const isEdit = !!flock;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mortalityLog, setMortalityLog] = useState([]);

  const [form, setForm] = useState({
    arrivalDate: '',
    totalBirds: '',
    arBirds: '',
    growerName: '',
    growerBirds: '',
    source: '',
    chickCostPerBird: '',
    brooderToPastureDate: '',
    brooderToPastureCount: '',
    pastureConfig: '2 large pens',
    butcherDate: '',
    preButcherCount: '',
    notes: '',
  });

  useEffect(() => {
    if (flock) {
      setForm(f => ({
        ...f,
        ...flock,
        arrivalDate: toDateInput(flock.arrivalDate),
        brooderToPastureDate: toDateInput(flock.brooderToPastureDate),
        butcherDate: toDateInput(flock.butcherDate),
      }));
      try {
        setMortalityLog(JSON.parse(flock.mortalityLog || '[]'));
      } catch {
        setMortalityLog([]);
      }
    }
  }, [flock]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  function addMortality() {
    setMortalityLog(prev => [...prev, emptyMortality()]);
  }

  function updateMortality(index, field, value) {
    setMortalityLog(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  }

  function removeMortality(index) {
    setMortalityLog(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (!form.arrivalDate) return setError('Arrival date is required.');
    if (!form.totalBirds) return setError('Total birds is required.');
    setError('');
    setSaving(true);

    const data = {
      ...form,
      mortalityLog: JSON.stringify(mortalityLog.filter(m => m.date || m.count)),
    };

    let res;
    if (isEdit) {
      res = await updateFlock(flock.flockId, data);
    } else {
      res = await createFlock(data);
    }

    setSaving(false);
    if (res.success) onSaved();
    else setError('Error saving: ' + res.error);
  }

  return (
    <div className="bl-form-container">
      <div className="bl-form-header">
        <h2>{isEdit ? 'Edit Flock' : 'New Flock'}</h2>
        <button className="bl-btn-secondary" onClick={onCancel}>← Back</button>
      </div>

      {error && <div className="bl-error">{error}</div>}

      <div className="bl-form-section">
        <div className="bl-form-section-title">Arrival</div>
        <div className="bl-form-row">
          <div className="bl-form-group">
            <label>Arrival Date</label>
            <input type="date" name="arrivalDate" value={form.arrivalDate} onChange={handleChange} />
          </div>
          <div className="bl-form-group">
            <label>Source / Hatchery</label>
            <input type="text" name="source" value={form.source} onChange={handleChange} placeholder="e.g. Murray McMurray" />
          </div>
          <div className="bl-form-group">
            <label>Chick Cost (per bird)</label>
            <input type="number" step="0.01" name="chickCostPerBird" value={form.chickCostPerBird} onChange={handleChange} placeholder="e.g. 3.50" />
          </div>
        </div>
        <div className="bl-form-row">
          <div className="bl-form-group">
            <label>Total Birds</label>
            <input type="number" name="totalBirds" value={form.totalBirds} onChange={handleChange} placeholder="e.g. 200" />
          </div>
          <div className="bl-form-group">
            <label>AR Birds</label>
            <input type="number" name="arBirds" value={form.arBirds} onChange={handleChange} placeholder="e.g. 120" />
          </div>
        </div>
      </div>

      <div className="bl-form-section">
        <div className="bl-form-section-title">Contract Grower</div>
        <div className="bl-form-row">
          <div className="bl-form-group">
            <label>Grower Name</label>
            <select name="growerName" value={form.growerName} onChange={handleChange}>
              <option value="">None</option>
              <option value="Max">Max</option>
              <option value="Jared">Jared</option>
            </select>
          </div>
          <div className="bl-form-group">
            <label>Grower Birds</label>
            <input type="number" name="growerBirds" value={form.growerBirds} onChange={handleChange} placeholder="e.g. 80" />
          </div>
        </div>
      </div>

      <div className="bl-form-section">
        <div className="bl-form-section-title">Brooder → Pasture</div>
        <div className="bl-form-row">
          <div className="bl-form-group">
            <label>Move to Pasture Date</label>
            <input type="date" name="brooderToPastureDate" value={form.brooderToPastureDate} onChange={handleChange} />
          </div>
          <div className="bl-form-group">
            <label>Count at Move</label>
            <input type="number" name="brooderToPastureCount" value={form.brooderToPastureCount} onChange={handleChange} placeholder="e.g. 195" />
          </div>
          <div className="bl-form-group">
            <label>Pasture Config</label>
            <select name="pastureConfig" value={form.pastureConfig} onChange={handleChange}>
              <option value="2 large pens">2 Large Pens</option>
              <option value="3 small pens">3 Small Pens</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bl-form-section">
        <div className="bl-form-section-title">Butcher Day</div>
        <div className="bl-form-row">
          <div className="bl-form-group">
            <label>Butcher Date</label>
            <input type="date" name="butcherDate" value={form.butcherDate} onChange={handleChange} />
          </div>
          <div className="bl-form-group">
            <label>Pre-Butcher Count</label>
            <input type="number" name="preButcherCount" value={form.preButcherCount} onChange={handleChange} placeholder="e.g. 188" />
          </div>
        </div>
      </div>

      <div className="bl-form-section">
        <div className="bl-form-section-title">Mortality Log</div>
        {mortalityLog.map((m, i) => (
          <div key={i} className="bl-mortality-row">
            <div className="bl-form-row">
              <div className="bl-form-group">
                <label>Date</label>
                <input type="date" value={m.date} onChange={e => updateMortality(i, 'date', e.target.value)} />
              </div>
              <div className="bl-form-group">
                <label>Count</label>
                <input type="number" value={m.count} onChange={e => updateMortality(i, 'count', e.target.value)} placeholder="0" />
              </div>
              <div className="bl-form-group">
                <label>Stage</label>
                <select value={m.stage} onChange={e => updateMortality(i, 'stage', e.target.value)}>
                  <option value="brooder">Brooder</option>
                  <option value="pasture">Pasture</option>
                  <option value="butcher">Butcher Day</option>
                </select>
              </div>
              <button className="bl-btn-remove" onClick={() => removeMortality(i)}>✕</button>
            </div>
            <div className="bl-form-group">
              <label>Cause</label>
              <input type="text" value={m.cause} onChange={e => updateMortality(i, 'cause', e.target.value)} placeholder="e.g. Predator loss, Heart failure, Chick failure" />
            </div>
          </div>
        ))}
        <button className="bl-btn-add" onClick={addMortality}>+ Add Mortality Entry</button>
      </div>

      <div className="bl-form-group">
        <label>Notes</label>
        <textarea name="notes" value={form.notes} onChange={handleChange} rows={3} placeholder="Any additional notes..." />
      </div>

      <div className="bl-form-actions">
        <button className="bl-btn-secondary" onClick={onCancel}>Cancel</button>
        <button className="bl-btn-primary" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Update Flock' : 'Save Flock'}
        </button>
      </div>
    </div>
  );
}
